/**
 * Shape of a single entry in the gateway's `skills.status` response.
 *
 * The gateway (OpenClaw) is the source of truth; it ingests each skill's
 * `SKILL.md` server-side and advertises this status metadata over the
 * WebSocket RPC. Every field is treated as `unknown` here because the
 * payload is author-controlled — consumers must narrow + fall back.
 */
export type SkillStatus = {
  readonly name?: unknown;
  readonly description?: unknown;
  readonly enabled?: unknown;
  readonly eligible?: unknown;
  readonly disabledReason?: unknown;
  readonly missingRequirements?: unknown;
};

export function isSkillStatus(value: unknown): value is SkillStatus {
  return value != null && typeof value === 'object';
}

/**
 * Normalize the `skills.status` payload into an array of skill entries.
 * The gateway may answer with either a bare array or `{ skills: [...] }`;
 * anything else yields an empty list.
 */
export function normalizeSkillStatusPayload(
  payload: unknown,
): readonly SkillStatus[] {
  if (Array.isArray(payload)) {
    return payload.filter(isSkillStatus);
  }

  if (payload != null && typeof payload === 'object') {
    const maybeSkills = (payload as { readonly skills?: unknown }).skills;
    if (Array.isArray(maybeSkills)) {
      return maybeSkills.filter(isSkillStatus);
    }
  }

  return [];
}
